import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { MicIcon, MapPinIcon, ListChecksIcon, HammerIcon, HardHatIcon, TagIcon, SparklesIcon, ShieldCheckIcon, SquareIcon, PlusIcon, MessageSquareIcon, BoltIcon, ChevronLeftIcon, CheckIcon, ChevronRightIcon, TreeDeciduousIcon, ZapIcon, DropletsIcon, Flower2Icon, BoxIcon, MessageCircleIcon, SearchIcon, WrenchIcon, Share2Icon, PhoneIcon, TriangleAlertIcon, BookOpenIcon, UserCheckIcon, StarIcon, ExternalLinkIcon, Trash2Icon } from 'lucide-react';
import { AssociateButton } from '../components/AssociateButton';
import { matchRonaProjects, ronaProjects, type RonaProject } from '../components/ronaProjects';
import { useAssociateStatus } from '../components/AssociateStatusContext';
import { useScreenInit } from '../useScreenInit';
import { useLanguage } from '../components/LanguageContext';
const projects = [{
  id: 'find',
  labelKey: 'welcome.find',
  icon: MapPinIcon,
  route: '/list'
}, {
  id: 'list',
  labelKey: 'welcome.list',
  icon: ListChecksIcon,
  route: '/chat',
  intent: 'shopping-list'
}, {
  id: 'diy',
  labelKey: 'welcome.diy',
  icon: HammerIcon,
  route: '/chat',
  intent: 'diy-instructions'
}, {
  id: 'contractor',
  labelKey: 'welcome.contractor',
  icon: HardHatIcon,
  route: '/chat',
  intent: 'contractor-quote'
}, {
  id: 'deals',
  labelKey: 'welcome.deals',
  icon: TagIcon,
  route: '/list',
  iconBg: 'bg-yellow-100',
  iconColor: 'text-yellow-600'
}];
type Message = {
  role: 'user' | 'ai';
  text: string;
};
type ListItem = {
  id: number;
  name: string;
  aisle: string;
  thumb: string;
  checked: boolean;
  dealCategory?: string;
};
type DealCategory = {
  id: string;
  labelKey: string;
  icon: typeof HammerIcon;
  bg: string;
  iconColor: string;
  count: number;
};
const dealCategories: DealCategory[] = [{
  id: 'tools',
  labelKey: 'deals.tools',
  icon: HammerIcon,
  bg: 'bg-blue-50',
  iconColor: 'text-blue-600',
  count: 12
}, {
  id: 'paint',
  labelKey: 'deals.paint',
  icon: BoxIcon,
  bg: 'bg-rose-50',
  iconColor: 'text-rose-600',
  count: 8
}, {
  id: 'lumber',
  labelKey: 'deals.lumber',
  icon: TreeDeciduousIcon,
  bg: 'bg-amber-50',
  iconColor: 'text-amber-700',
  count: 6
}, {
  id: 'electrical',
  labelKey: 'deals.electrical',
  icon: ZapIcon,
  bg: 'bg-yellow-50',
  iconColor: 'text-yellow-600',
  count: 5
}, {
  id: 'plumbing',
  labelKey: 'deals.plumbing',
  icon: DropletsIcon,
  bg: 'bg-cyan-50',
  iconColor: 'text-cyan-600',
  count: 9
}, {
  id: 'garden',
  labelKey: 'deals.garden',
  icon: Flower2Icon,
  bg: 'bg-emerald-50',
  iconColor: 'text-emerald-600',
  count: 11
}];
type DealProduct = {
  id: number;
  name: string;
  aisle: string;
  thumb: string;
  msrp: number;
  promo: number;
};
const dealProductsByCategory: Record<string, DealProduct[]> = {
  tools: [{
    id: 1,
    name: 'DeWalt 20V Cordless Drill',
    aisle: 'Aisle 21',
    thumb: 'bg-yellow-500',
    msrp: 199.99,
    promo: 139.99
  }, {
    id: 2,
    name: 'Stanley 16oz Claw Hammer',
    aisle: 'Aisle 22',
    thumb: 'bg-zinc-700',
    msrp: 29.99,
    promo: 19.99
  }, {
    id: 3,
    name: '12-Piece Screwdriver Set',
    aisle: 'Aisle 22',
    thumb: 'bg-red-500',
    msrp: 39.99,
    promo: 24.99
  }, {
    id: 4,
    name: 'Circular Saw 7-1/4"',
    aisle: 'Aisle 23',
    thumb: 'bg-orange-500',
    msrp: 159.99,
    promo: 109.99
  }, {
    id: 5,
    name: '25ft Measuring Tape',
    aisle: 'Aisle 22',
    thumb: 'bg-yellow-400',
    msrp: 17.99,
    promo: 9.99
  }],
  paint: [{
    id: 1,
    name: 'Behr Premium Plus Interior 1gal',
    aisle: 'Aisle 15',
    thumb: 'bg-rose-300',
    msrp: 38.99,
    promo: 27.99
  }, {
    id: 2,
    name: 'Pro 3" Angle Paint Brush',
    aisle: 'Aisle 15',
    thumb: 'bg-amber-300',
    msrp: 14.99,
    promo: 8.99
  }, {
    id: 3,
    name: 'Microfiber Roller 9"',
    aisle: 'Aisle 15',
    thumb: 'bg-slate-300',
    msrp: 11.99,
    promo: 6.99
  }, {
    id: 4,
    name: "Painter's Tape 1.88in x 60yd",
    aisle: 'Aisle 15',
    thumb: 'bg-blue-300',
    msrp: 8.99,
    promo: 5.49
  }],
  lumber: [{
    id: 1,
    name: '2x4x8 Pressure Treated',
    aisle: 'Aisle 18',
    thumb: 'bg-amber-700',
    msrp: 9.99,
    promo: 6.99
  }, {
    id: 2,
    name: '4x4x8 Cedar Post',
    aisle: 'Aisle 4',
    thumb: 'bg-amber-800',
    msrp: 24.99,
    promo: 17.99
  }, {
    id: 3,
    name: '5/4 x 6 Composite Decking',
    aisle: 'Aisle 18',
    thumb: 'bg-stone-500',
    msrp: 38.99,
    promo: 28.99
  }, {
    id: 4,
    name: '3/4" Plywood 4x8 Sheet',
    aisle: 'Aisle 17',
    thumb: 'bg-amber-600',
    msrp: 64.99,
    promo: 49.99
  }],
  electrical: [{
    id: 1,
    name: '12/2 Romex Wire 50ft',
    aisle: 'Aisle 28',
    thumb: 'bg-orange-500',
    msrp: 64.99,
    promo: 44.99
  }, {
    id: 2,
    name: 'LED A19 Bulb 4-Pack',
    aisle: 'Aisle 29',
    thumb: 'bg-yellow-200',
    msrp: 18.99,
    promo: 9.99
  }, {
    id: 3,
    name: 'Smart Light Switch',
    aisle: 'Aisle 29',
    thumb: 'bg-slate-200',
    msrp: 39.99,
    promo: 24.99
  }, {
    id: 4,
    name: 'GFCI Outlet 15A',
    aisle: 'Aisle 28',
    thumb: 'bg-zinc-300',
    msrp: 22.99,
    promo: 14.99
  }],
  plumbing: [{
    id: 1,
    name: '1/2" PVC Pipe 10ft',
    aisle: 'Aisle 31',
    thumb: 'bg-slate-100',
    msrp: 7.99,
    promo: 4.99
  }, {
    id: 2,
    name: 'Chrome Bath Faucet',
    aisle: 'Aisle 32',
    thumb: 'bg-zinc-400',
    msrp: 119.99,
    promo: 79.99
  }, {
    id: 3,
    name: 'Wax Toilet Ring',
    aisle: 'Aisle 32',
    thumb: 'bg-amber-200',
    msrp: 4.99,
    promo: 2.99
  }, {
    id: 4,
    name: 'Silicone Caulk Tube',
    aisle: 'Aisle 9',
    thumb: 'bg-slate-200',
    msrp: 9.99,
    promo: 5.99
  }, {
    id: 5,
    name: 'Pipe Wrench 14"',
    aisle: 'Aisle 31',
    thumb: 'bg-red-500',
    msrp: 34.99,
    promo: 22.99
  }],
  garden: [{
    id: 1,
    name: 'Cubic Yard Mulch',
    aisle: 'Aisle 36',
    thumb: 'bg-amber-700',
    msrp: 6.99,
    promo: 3.99
  }, {
    id: 2,
    name: 'Garden Shovel',
    aisle: 'Aisle 35',
    thumb: 'bg-emerald-700',
    msrp: 29.99,
    promo: 17.99
  }, {
    id: 3,
    name: '50ft Garden Hose',
    aisle: 'Aisle 35',
    thumb: 'bg-green-500',
    msrp: 44.99,
    promo: 27.99
  }, {
    id: 4,
    name: 'All-Purpose Fertilizer',
    aisle: 'Aisle 36',
    thumb: 'bg-lime-600',
    msrp: 19.99,
    promo: 12.99
  }, {
    id: 5,
    name: 'Solar Garden Lights 6pk',
    aisle: 'Aisle 36',
    thumb: 'bg-yellow-300',
    msrp: 34.99,
    promo: 19.99
  }]
};
const generatedListItems: ListItem[] = [{
  id: 1,
  name: '4x4 Cedar Posts',
  aisle: 'Aisle 4',
  thumb: 'bg-amber-800',
  checked: false,
  dealCategory: 'lumber'
}, {
  id: 2,
  name: 'Joist Hangers',
  aisle: 'Aisle 12',
  thumb: 'bg-slate-400',
  checked: false
}, {
  id: 3,
  name: '3" Deck Screws',
  aisle: 'Aisle 7',
  thumb: 'bg-zinc-600',
  checked: false
}, {
  id: 4,
  name: 'Pressure Treated Boards',
  aisle: 'Aisle 18',
  thumb: 'bg-amber-600',
  checked: false,
  dealCategory: 'lumber'
}, {
  id: 5,
  name: 'Wood Sealant',
  aisle: 'Aisle 9',
  thumb: 'bg-orange-200',
  checked: false
}];
export function Welcome() {
  useScreenInit();
  const navigate = useNavigate();
  const {
    t
  } = useLanguage();
  const {
    status: associateStatus,
    reset: resetAssociate
  } = useAssociateStatus();
  const [associateArrived, setAssociateArrived] = useState(false);
  useEffect(() => {
    if (associateStatus === 'idle') setAssociateArrived(false);
  }, [associateStatus]);
  const handleAssociateArrived = () => {
    // Complete the status bar, then dismiss the request
    setAssociateArrived(true);
    setTimeout(() => resetAssociate(), 900);
  };
  const [mode, setMode] = useState<'menu' | 'chat' | 'list' | 'deals' | 'deals-products' | 'find' | 'diy-steps' | 'contractor-summary' | 'diy-guide-detail'>('menu');
  const [isRecording, setIsRecording] = useState(false);
  const [inputMode, setInputMode] = useState<'voice' | 'text'>('voice');
  const [textInput, setTextInput] = useState('');
  const [messages, setMessages] = useState<Message[]>([]);
  const [listItems, setListItems] = useState<ListItem[]>(generatedListItems);
  const [selectedDealCategory, setSelectedDealCategory] = useState<DealCategory | null>(null);
  const [chatIntent, setChatIntent] = useState<'shopping-list' | 'contractor' | 'diy' | 'associate-wait' | 'add-more'>('shopping-list');
  // When leaving the list to reopen the chat, slide it up & off the top
  const [listExitUp, setListExitUp] = useState(false);
  // Additional items "heard" when adding more to an existing list
  const addMoreItems = (): ListItem[] => [{
    id: Date.now(),
    name: 'Outdoor String Lights',
    aisle: 'Aisle 29',
    thumb: 'bg-yellow-300',
    checked: false
  }, {
    id: Date.now() + 1,
    name: 'Galvanized Deck Brackets',
    aisle: 'Aisle 12',
    thumb: 'bg-slate-400',
    checked: false
  }, {
    id: Date.now() + 2,
    name: 'Exterior Wood Stain',
    aisle: 'Aisle 9',
    thumb: 'bg-amber-700',
    checked: false
  }];
  const [shared, setShared] = useState(false);
  const [contractorPhone, setContractorPhone] = useState('');
  const [contractorZip, setContractorZip] = useState('');
  const [contractorSubmitted, setContractorSubmitted] = useState(false);
  const [findQuery, setFindQuery] = useState('');
  const [findFocused, setFindFocused] = useState(false);
  const findCatalog = [{
    name: 'Deck Screws 3"',
    aisle: 'Aisle 7',
    thumb: 'bg-zinc-600',
    icon: BoltIcon
  }, {
    name: 'Deck Screws 2.5"',
    aisle: 'Aisle 7',
    thumb: 'bg-zinc-500',
    icon: BoltIcon
  }, {
    name: 'Galvanized Deck Brackets',
    aisle: 'Aisle 12',
    thumb: 'bg-slate-400',
    icon: WrenchIcon
  }, {
    name: 'Claw Hammer',
    aisle: 'Aisle 22',
    thumb: 'bg-zinc-700',
    icon: HammerIcon
  }, {
    name: 'Cordless Drill',
    aisle: 'Aisle 21',
    thumb: 'bg-yellow-500',
    icon: ZapIcon
  }, {
    name: 'Interior Paint',
    aisle: 'Aisle 15',
    thumb: 'bg-rose-300',
    icon: BoxIcon
  }, {
    name: 'Paint Brush',
    aisle: 'Aisle 15',
    thumb: 'bg-amber-300',
    icon: BoxIcon
  }, {
    name: 'Pine Lumber 2x4',
    aisle: 'Aisle 18',
    thumb: 'bg-amber-600',
    icon: TreeDeciduousIcon
  }, {
    name: 'Cedar Posts 4x4',
    aisle: 'Aisle 4',
    thumb: 'bg-amber-800',
    icon: TreeDeciduousIcon
  }, {
    name: 'Wood Sealant',
    aisle: 'Aisle 9',
    thumb: 'bg-orange-200',
    icon: DropletsIcon
  }];
  const filteredCatalog = findCatalog.filter(item => item.name.toLowerCase().includes(findQuery.toLowerCase()));
  // Seed the AI greeting once we enter chat mode
  useEffect(() => {
    if (mode === 'chat' && messages.length === 0) {
      // Delay so the menu slide-up finishes first
      const timer = setTimeout(() => {
        setMessages([{
          role: 'ai',
          text: chatIntent === 'contractor' ? t('welcome.aiGreetingContractor') : chatIntent === 'diy' ? t('welcome.aiGreetingDiy') : chatIntent === 'associate-wait' ? t('welcome.aiGreetingAssociate') : chatIntent === 'add-more' ? t('welcome.aiGreetingAddMore') : t('welcome.aiGreeting')
        }]);
      }, 350);
      return () => clearTimeout(timer);
    }
  }, [mode, messages.length, t, chatIntent]);
  const handleTalkToAI = (intent: 'shopping-list' | 'contractor' | 'diy' | 'associate-wait' = 'shopping-list') => {
    if (mode === 'chat') return;
    setChatIntent(intent);
    setMode('chat');
    // Auto-start recording once the morph animation has settled
    setTimeout(() => setIsRecording(true), 700);
  };
  const handleBackToMenu = () => {
    setMode('menu');
    setIsRecording(false);
    setMessages([]);
    setListItems(generatedListItems);
    setSelectedDealCategory(null);
    setChatIntent('shopping-list');
    setListExitUp(false);
    setShared(false);
    setContractorPhone('');
    setContractorZip('');
    setContractorSubmitted(false);
  };
  const handleContractorSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!contractorPhone.trim() || !contractorZip.trim()) return;
    setContractorSubmitted(true);
  };
  // Reset to the home menu when the RONA logo is tapped (from any in-page flow)
  useEffect(() => {
    const goHome = () => handleBackToMenu();
    window.addEventListener('mp:go-home', goHome);
    return () => window.removeEventListener('mp:go-home', goHome);
  }, []);
  const handleShareInstructions = async () => {
    const detailSteps = [{
      title: t('welcome.diyDetail1Title'),
      body: t('welcome.diyDetail1Body'),
      tip: t('welcome.diyDetail1Tip')
    }, {
      title: t('welcome.diyDetail2Title'),
      body: t('welcome.diyDetail2Body'),
      warn: t('welcome.diyDetail2Warn')
    }, {
      title: t('welcome.diyDetail3Title'),
      body: t('welcome.diyDetail3Body')
    }, {
      title: t('welcome.diyDetail4Title'),
      body: t('welcome.diyDetail4Body'),
      tip: t('welcome.diyDetail4Tip')
    }, {
      title: t('welcome.diyDetail5Title'),
      body: t('welcome.diyDetail5Body')
    }, {
      title: t('welcome.diyDetail6Title'),
      body: t('welcome.diyDetail6Body'),
      warn: t('welcome.diyDetail6Warn')
    }];
    const fullText = [t('welcome.diyStepsTitle'), t('welcome.diyStepsSubtitle'), '', `${t('welcome.diyGuideTools').toUpperCase()}`, t('welcome.diyGuideToolsList'), '', `${t('welcome.diyGuideStepsLabel').toUpperCase()}`, ...detailSteps.map(s => {
      let block = `\n${s.title}\n${s.body}`;
      if (s.tip) block += `\n${t('welcome.diyGuideTip')}: ${s.tip}`;
      if (s.warn) block += `\n${t('welcome.diyGuideWarn')}: ${s.warn}`;
      return block;
    }), '', t('welcome.diyPoweredBy')].join('\n');
    const shareData = {
      title: t('welcome.diyStepsTitle'),
      text: fullText
    };
    try {
      if (typeof navigator !== 'undefined' && navigator.share) {
        await navigator.share(shareData);
      }
      setShared(true);
      setTimeout(() => setShared(false), 2500);
    } catch {
      // user cancelled or share unsupported — show copied confirmation as fallback
      setShared(true);
      setTimeout(() => setShared(false), 2500);
    }
  };
  const handleSelectDealCategory = (category: DealCategory) => {
    setSelectedDealCategory(category);
    setMode('deals-products');
  };
  const handleBackToDeals = () => {
    setMode('deals');
    setSelectedDealCategory(null);
  };
  const toggleListItem = (id: number) => {
    setListItems(prev => prev.map(item => item.id === id ? {
      ...item,
      checked: !item.checked
    } : item));
  };
  const removeListItem = (id: number) => {
    setListItems(prev => prev.filter(item => item.id !== id));
  };
  // Surface matching RONA Workshop DIY guides based on what the user described
  const lastUserText = [...messages].reverse().find(m => m.role === 'user')?.text ?? '';
  const ronaMatches = matchRonaProjects(lastUserText);
  const ronaSuggestions: RonaProject[] = ronaMatches.length > 0 ? ronaMatches : ronaProjects.filter(p => p.id === 'deck');
  const renderRonaGuides = () => <div className="mt-3 rounded-2xl border border-slate-200 bg-white shadow-[0_2px_12px_rgba(15,23,42,0.04)] overflow-hidden" data-id="element-228">
      <div className="divide-y divide-slate-100 max-h-[34vh] overflow-y-auto" data-id="element-229">
        {ronaSuggestions.map(p => <a key={p.id} href={p.url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 px-4 py-2.5 hover:bg-slate-50 transition-colors group" data-id="element-230">
            <div className="flex-1 min-w-0" data-id="element-231">
              <p className="text-[13px] font-semibold text-slate-900 leading-tight group-hover:text-blue-600 transition-colors" data-id="element-232">
                {p.title}
              </p>
              <p className="text-[11px] text-slate-500 leading-snug mt-0.5 truncate" data-id="element-233">
                {p.blurb}
              </p>
            </div>
            <ExternalLinkIcon size={15} className="text-slate-300 group-hover:text-blue-600 shrink-0 transition-colors" data-id="element-234" />
          </a>)}
      </div>
      <div className="px-4 py-2 bg-slate-50 border-t border-slate-100" data-id="element-235">
        <span className="text-[10px] font-medium text-slate-400" data-id="element-236">
          {t('welcome.ronaGuidesSource')}
        </span>
      </div>
    </div>;
  const handleMicTap = () => {
    if (isRecording) {
      setIsRecording(false);
      if (chatIntent === 'contractor') {
        setMessages(prev => [...prev, {
          role: 'user',
          text: 'I want to add a deck to the back of my house. Roughly 12x16, attached to the back door.'
        }]);
        setTimeout(() => {
          setMessages(prev => [...prev, {
            role: 'ai',
            text: t('welcome.aiConfirmContractor')
          }]);
          // Transition chat → project summary + connecting screen
          setTimeout(() => setMode('contractor-summary'), 1400);
        }, 900);
      } else if (chatIntent === 'diy') {
        setMessages(prev => [...prev, {
          role: 'user',
          text: 'I want to install a new bathroom faucet myself.'
        }]);
        setTimeout(() => {
          setMessages(prev => [...prev, {
            role: 'ai',
            text: t('welcome.aiConfirmDiy')
          }]);
          // Transition chat → step-by-step instructions in place
          setTimeout(() => setMode('diy-steps'), 1400);
        }, 900);
      } else if (chatIntent === 'add-more') {
        // Adding more items to an existing list
        setMessages(prev => [...prev, {
          role: 'user',
          text: 'Can you also add some string lights, deck brackets and exterior stain?'
        }]);
        setTimeout(() => {
          setMessages(prev => [...prev, {
            role: 'ai',
            text: t('welcome.aiConfirmAddMore')
          }]);
          // Append the new items to the existing list, then slide back to it
          setListItems(prev => [...addMoreItems(), ...prev]);
          setTimeout(() => {
            setMode('list');
            setListExitUp(false);
          }, 1400);
        }, 900);
      } else {
        // shopping-list flow
        setMessages(prev => [...prev, {
          role: 'user',
          text: "I'm building a 12x16 deck attached to my back door."
        }]);
        setTimeout(() => {
          setMessages(prev => [...prev, {
            role: 'ai',
            text: "Perfect. I'm putting together your shopping list now — give me one second..."
          }]);
          // Transition chat → list in place (no route change)
          setTimeout(() => setMode('list'), 1400);
        }, 900);
      }
    } else if (mode === 'list') {
      // Tapping the mic on the list reopens the chat to add more items;
      // the list slides up & off the top.
      setListExitUp(true);
      setMessages([]);
      setChatIntent('add-more');
      setMode('chat');
      setTimeout(() => setIsRecording(true), 700);
    } else {
      setIsRecording(true);
    }
  };
  return <motion.div initial={{
    opacity: 0,
    y: 10
  }} animate={{
    opacity: 1,
    y: 0
  }} className="px-6 pb-6 pt-2 flex flex-col h-full" data-id="element-237">
      {/* TOP / WHITE AREA */}
      <div className="flex-1 flex flex-col relative overflow-hidden" data-id="element-238">
        <AnimatePresence mode="popLayout" data-id="element-239">
          {mode === 'menu' ? <motion.div key="menu" initial={{
          y: 0
        }} animate={{
          y: 0
        }} exit={{
          y: '-120%',
          transition: {
            duration: 0.55,
            ease: [0.7, 0, 0.84, 0]
          }
        }} className="absolute inset-0 bg-white flex flex-col overflow-y-auto" data-id="element-240">
              <div className="mt-3 mb-4 text-center" data-id="element-241">
                <h1 className="text-2xl font-bold text-slate-900 tracking-tight leading-tight" data-id="element-242">
                  {t('welcome.headline')}
                </h1>
              </div>

              <div className="flex flex-col gap-2" data-id="element-243">
                {projects.map((project, idx) => <motion.button key={project.id} onClick={() => {
              if (project.id === 'deals') {
                setMode('deals');
                return;
              }
              if (project.id === 'list') {
                handleTalkToAI('shopping-list');
                return;
              }
              if (project.id === 'contractor') {
                handleTalkToAI('contractor');
                return;
              }
              if (project.id === 'diy') {
                handleTalkToAI('diy');
                return;
              }
              if (project.id === 'find') {
                setMode('find');
                return;
              }
              navigate(project.route, {
                state: {
                  intent: (project as any).intent
                }
              });
            }} layoutId={project.id === 'find' ? 'find-search-bar' : undefined} initial={{
              opacity: 0,
              y: 8
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              delay: idx * 0.03
            }} className="relative flex items-center gap-4 py-[10px] px-4 rounded-2xl border border-slate-100 bg-white shadow-[0_2px_12px_rgba(15,23,42,0.04)] hover:border-blue-200 hover:shadow-md transition-all text-slate-700 hover:text-blue-600 text-left" data-id="element-244">
                    {project.id === 'list' && <span className="absolute -top-2.5 right-3 z-10 flex items-center gap-1 bg-blue-700 text-white text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full shadow-[0_2px_8px_rgba(15,23,42,0.18)] ring-2 ring-white" data-id="element-245">
                        <SparklesIcon size={10} className="text-yellow-300" data-id="element-246" />
                        {t('welcome.mostPopular')}
                      </span>}
                    <div className={`w-10 h-10 rounded-xl flex flex-col items-center justify-center shrink-0 ${(project as any).iconBg ?? 'bg-blue-50'} ${(project as any).iconColor ?? 'text-blue-600'}`} data-id="element-247">
                      <project.icon size={project.id === 'contractor' ? 18 : 20} strokeWidth={2} fill={(project as any).iconBg ? 'currentColor' : 'none'} data-id="element-248" />
                      {project.id === 'contractor' && <span className="flex mt-[1px]" data-id="element-249">
                          {[0, 1, 2, 3, 4].map(s => <StarIcon key={s} size={5} className="text-amber-400 fill-amber-400" data-id="element-250" />)}
                        </span>}
                    </div>
                    <span className="font-medium text-[15px] flex-1" data-id="element-251">
                      {t(project.labelKey)}
                    </span>
                  </motion.button>)}
              </div>

              <div className="flex-1" data-id="element-252" />
            </motion.div> : mode === 'chat' ? <motion.div key="chat" initial={{
          y: '100%'
        }} animate={{
          y: 0,
          transition: {
            duration: 0.55,
            ease: [0.16, 1, 0.3, 1]
          }
        }} exit={{
          y: '-120%',
          transition: {
            duration: 0.55,
            ease: [0.7, 0, 0.84, 0]
          }
        }} className="absolute inset-0 bg-white flex flex-col overflow-y-auto pt-2 pb-4 space-y-3" data-id="element-253">
              <button onClick={handleBackToMenu} className="self-start flex items-center gap-1 -ml-2 px-2 py-1.5 rounded-full text-slate-600 hover:bg-slate-100 transition-colors text-sm font-medium" aria-label="Back to options" data-id="element-254">
                <ChevronLeftIcon size={18} data-id="element-255" />
                Back
              </button>
              {messages.map((msg, idx) => <motion.div key={idx} initial={{
            opacity: 0,
            y: 8
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.3
          }} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`} data-id="element-256">
                  <div className={`max-w-[85%] rounded-2xl p-4 ${msg.role === 'user' ? 'bg-blue-600 text-white rounded-tr-sm' : 'bg-slate-100 text-slate-900 rounded-tl-sm'}`} data-id="element-257">
                    <p className="text-[15px] leading-relaxed" data-id="element-258">{msg.text}</p>
                  </div>
                </motion.div>)}
              {isRecording && <motion.div initial={{
            opacity: 0
          }} animate={{
            opacity: 1
          }} className="flex justify-end" data-id="element-259">
                  <div className="max-w-[85%] rounded-2xl p-4 bg-blue-600 text-white rounded-tr-sm flex items-center gap-2" data-id="element-260">
                    <span className="w-2 h-2 bg-white rounded-full animate-bounce" style={{
                animationDelay: '0ms'
              }} data-id="element-261"></span>
                    <span className="w-2 h-2 bg-white rounded-full animate-bounce" style={{
                animationDelay: '150ms'
              }} data-id="element-262"></span>
                    <span className="w-2 h-2 bg-white rounded-full animate-bounce" style={{
                animationDelay: '300ms'
              }} data-id="element-263"></span>
                  </div>
                </motion.div>}
            </motion.div> : mode === 'deals' ? <motion.div key="deals" initial={{
          y: '100%'
        }} animate={{
          y: 0,
          transition: {
            duration: 0.55,
            ease: [0.16, 1, 0.3, 1]
          }
        }} exit={selectedDealCategory ? {
          y: '-120%',
          transition: {
            duration: 0.5,
            ease: [0.7, 0, 0.84, 0]
          }
        } : {
          y: '100%',
          transition: {
            duration: 0.4,
            ease: 'easeIn'
          }
        }} className="absolute inset-0 bg-white flex flex-col overflow-y-auto pt-2 pb-4" data-id="element-264">
              <div className="flex items-center justify-between mb-2" data-id="element-265">
                <button onClick={handleBackToMenu} className="flex items-center gap-1 -ml-2 px-2 py-1.5 rounded-full text-slate-600 hover:bg-slate-100 transition-colors text-sm font-medium" aria-label="Back to options" data-id="element-266">
                  <ChevronLeftIcon size={18} data-id="element-267" />
                  Back
                </button>
              </div>

              <div className="mb-4" data-id="element-268">
                <h2 className="text-2xl font-bold text-slate-900 tracking-tight leading-tight" data-id="element-269">
                  {t('welcome.dealsTitle')}
                </h2>
                <p className="text-slate-500 text-xs mt-0.5" data-id="element-270">
                  {t('welcome.dealsSubtitle')}
                </p>
              </div>

              <div className="grid grid-cols-2 gap-2.5" data-id="element-271">
                {dealCategories.map((category, idx) => <motion.button key={category.id} onClick={() => handleSelectDealCategory(category)} initial={{
              opacity: 0,
              y: 8
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              delay: 0.2 + idx * 0.05
            }} className="aspect-square flex flex-col items-center justify-center gap-1.5 p-3 rounded-2xl border border-slate-100 bg-white shadow-[0_2px_12px_rgba(15,23,42,0.04)] hover:border-blue-200 hover:shadow-md transition-all text-slate-700 hover:text-blue-600 relative" data-id="element-272">
                    <span className="absolute top-2 right-2 inline-flex items-center gap-1 text-[10px] font-bold text-yellow-700 bg-yellow-100 border border-yellow-200 rounded-full px-1.5 py-0.5" data-id="element-273">
                      <TagIcon size={9} fill="currentColor" data-id="element-274" />
                      {category.count}
                    </span>
                    <div className={`w-12 h-12 rounded-2xl flex items-center justify-center ${category.bg} ${category.iconColor}`} data-id="element-275">
                      <category.icon size={24} strokeWidth={2} data-id="element-276" />
                    </div>
                    <span className="font-semibold text-sm" data-id="element-277">
                      {t(category.labelKey)}
                    </span>
                    <span className="text-[11px] text-slate-400" data-id="element-278">
                      {category.count} {t('deals.dealsCount')}
                    </span>
                  </motion.button>)}
              </div>
            </motion.div> : mode === 'deals-products' && selectedDealCategory ? <motion.div key="deals-products" initial={{
          y: '100%'
        }} animate={{
          y: 0,
          transition: {
            duration: 0.55,
            ease: [0.16, 1, 0.3, 1]
          }
        }} exit={{
          y: '100%',
          transition: {
            duration: 0.4,
            ease: 'easeIn'
          }
        }} className="absolute inset-0 bg-white flex flex-col overflow-y-auto pt-2 pb-4" data-id="element-279">
              <div className="flex items-center justify-between mb-2" data-id="element-280">
                <button onClick={handleBackToDeals} className="flex items-center gap-1 -ml-2 px-2 py-1.5 rounded-full text-slate-600 hover:bg-slate-100 transition-colors text-sm font-medium" aria-label="Back to categories" data-id="element-281">
                  <ChevronLeftIcon size={18} data-id="element-282" />
                  Back
                </button>
              </div>

              <div className="mb-4 flex items-center gap-3" data-id="element-283">
                <div className={`w-11 h-11 rounded-2xl flex items-center justify-center shrink-0 ${selectedDealCategory.bg} ${selectedDealCategory.iconColor}`} data-id="element-284">
                  <selectedDealCategory.icon size={22} strokeWidth={2} data-id="element-285" />
                </div>
                <div className="flex-1 min-w-0" data-id="element-286">
                  <h2 className="text-xl font-bold text-slate-900 tracking-tight leading-tight" data-id="element-287">
                    {t(selectedDealCategory.labelKey)}{' '}
                    {t('deals.productsTitle')}
                  </h2>
                  <p className="text-slate-500 text-xs mt-0.5" data-id="element-288">
                    {(dealProductsByCategory[selectedDealCategory.id] ?? []).length}{' '}
                    {t('deals.dealsCount')}
                  </p>
                </div>
              </div>

              <div className="flex flex-col gap-2" data-id="element-289">
                {(dealProductsByCategory[selectedDealCategory.id] ?? []).map((product, idx) => {
              const pctOff = Math.round((product.msrp - product.promo) / product.msrp * 100);
              return <motion.button key={product.id} initial={{
                opacity: 0,
                y: 8
              }} animate={{
                opacity: 1,
                y: 0
              }} transition={{
                delay: 0.15 + idx * 0.05
              }} onClick={() => navigate('/product')} className="flex items-center gap-3 p-3 rounded-2xl border border-slate-100 bg-white shadow-[0_2px_12px_rgba(15,23,42,0.04)] hover:border-blue-200 hover:shadow-md transition-all text-left relative" data-id="element-290">
                        <div className={`w-12 h-12 rounded-xl shrink-0 ${product.thumb} opacity-80`} data-id="element-291" />
                        <div className="flex-1 min-w-0" data-id="element-292">
                          <p className="text-[14px] font-semibold text-slate-900 truncate leading-tight" data-id="element-293">
                            {product.name}
                          </p>
                          <div className="flex items-center text-blue-600 text-[11px] font-medium mt-0.5" data-id="element-294">
                            <MapPinIcon size={10} className="mr-0.5" data-id="element-295" />
                            {product.aisle}
                          </div>
                          <div className="flex items-baseline gap-1.5 mt-1" data-id="element-296">
                            <span className="text-base font-bold text-red-600" data-id="element-297">
                              ${product.promo.toFixed(2)}
                            </span>
                            <span className="text-[11px] text-slate-400 line-through" data-id="element-298">
                              {t('deals.msrp')} ${product.msrp.toFixed(2)}
                            </span>
                          </div>
                        </div>
                        <span className="self-start shrink-0 inline-flex items-center gap-0.5 text-[10px] font-bold text-white bg-red-500 rounded-full px-2 py-0.5" data-id="element-299">
                          {pctOff}% {t('deals.off')}
                        </span>
                      </motion.button>;
            })}
              </div>
            </motion.div> : mode === 'find' ? <motion.div key="find" initial={{
          opacity: 0
        }} animate={{
          opacity: 1,
          transition: {
            duration: 0.4
          }
        }} exit={{
          opacity: 0,
          transition: {
            duration: 0.3
          }
        }} className="absolute inset-0 bg-white flex flex-col overflow-hidden pt-2 pb-4" data-id="element-300">
              <div className="flex items-center justify-between mb-3 shrink-0" data-id="element-301">
                <button onClick={handleBackToMenu} className="flex items-center gap-1 -ml-2 px-2 py-1.5 rounded-full text-slate-600 hover:bg-slate-100 transition-colors text-sm font-medium" aria-label="Back to options" data-id="element-302">
                  <ChevronLeftIcon size={18} data-id="element-303" />
                  Back
                </button>
              </div>

              {/* Morphing search bar — shares layoutId with the Find tile in menu mode */}
              <motion.div layoutId="find-search-bar" transition={{
            type: 'spring',
            damping: 26,
            stiffness: 220
          }} className="flex items-center gap-2 bg-white border-2 border-blue-100 rounded-2xl pl-3 pr-2 py-2.5 shadow-[0_2px_12px_rgba(15,23,42,0.04)] focus-within:border-blue-300 focus-within:ring-2 focus-within:ring-blue-100 transition-all shrink-0 mb-3" data-id="element-304">
                <SearchIcon size={18} className="text-blue-600 shrink-0" data-id="element-305" />
                <input type="text" value={findQuery} onChange={e => setFindQuery(e.target.value)} onFocus={() => setFindFocused(true)} placeholder={t('list.searchPlaceholder')} className="flex-1 bg-transparent outline-none text-[14px] text-slate-900 placeholder:text-slate-400 py-1 min-w-0" autoFocus data-id="element-306" />
                {findQuery.trim().length > 0 ? <button type="button" onClick={() => {
              setFindQuery('');
              setFindFocused(false);
            }} className="px-3 py-1.5 text-sm font-medium text-slate-500 hover:text-slate-800 transition-colors" data-id="element-307">
                    Cancel
                  </button> : <motion.button whileTap={{
              scale: 0.95
            }} type="button" onClick={handleTalkToAI} className="w-9 h-9 rounded-xl bg-blue-600 hover:bg-blue-700 text-white flex items-center justify-center shrink-0 transition-colors" aria-label="Talk to AI" data-id="element-308">
                    <MicIcon size={18} data-id="element-309" />
                  </motion.button>}
              </motion.div>

              {findQuery.trim().length > 0 ? <div className="flex-1 overflow-y-auto" data-id="element-310">
                  <div className="bg-white rounded-2xl border border-slate-200 shadow-[0_2px_12px_rgba(15,23,42,0.04)] overflow-hidden" data-id="element-311">
                    <div className="divide-y divide-slate-100" data-id="element-312">
                      {filteredCatalog.length > 0 ? filteredCatalog.map((item, idx) => <div key={idx} className="px-4 py-3 flex items-center gap-3 bg-white" data-id="element-313">
                            <div className={`w-10 h-10 rounded-xl ${item.thumb} flex items-center justify-center text-white shrink-0 shadow-sm`} data-id="element-314">
                              <item.icon size={20} data-id="element-315" />
                            </div>
                            <div className="flex-1 min-w-0" data-id="element-316">
                              <p className="text-[14px] font-semibold text-slate-900 leading-tight truncate" data-id="element-317">
                                {item.name}
                              </p>
                              <div className="flex items-center text-blue-600 text-[11px] font-medium mt-1" data-id="element-318">
                                <MapPinIcon size={10} className="mr-0.5" data-id="element-319" />
                                {item.aisle}
                              </div>
                            </div>
                            <button onClick={() => {
                    setListItems(prev => [{
                      id: Date.now(),
                      name: item.name,
                      aisle: item.aisle,
                      thumb: item.thumb,
                      checked: false
                    }, ...prev]);
                    setFindQuery('');
                    setFindFocused(false);
                  }} className="w-8 h-8 rounded-full bg-slate-100 hover:bg-blue-50 text-slate-600 hover:text-blue-600 flex items-center justify-center transition-colors shrink-0" aria-label="Add to list" data-id="element-320">
                              <PlusIcon size={18} data-id="element-321" />
                            </button>
                          </div>) : <div className="p-6 text-center text-slate-500 text-sm" data-id="element-322">
                          No products found.
                        </div>}
                    </div>
                  </div>
                </div> /* Shopping list slides up from the bottom into the white area */ : <motion.div initial={{
            y: '100%',
            opacity: 0
          }} animate={{
            y: 0,
            opacity: 1,
            transition: {
              duration: 0.55,
              ease: [0.16, 1, 0.3, 1],
              delay: 0.15
            }
          }} className="flex-1 overflow-y-auto" data-id="element-323">
                  <h2 className="flex items-baseline gap-2 mb-2 px-1" data-id="element-324">
                    <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider" data-id="element-325">
                      {t('list.sectionTitle')}
                    </span>
                    <span className="text-[10px] font-medium text-slate-400 normal-case tracking-normal" data-id="element-326">
                      {t('list.swipeToDelete')}
                    </span>
                  </h2>
                  <div className="bg-white rounded-2xl border border-slate-200 shadow-[0_2px_12px_rgba(15,23,42,0.04)] overflow-hidden mr-5" data-id="element-327">
                    <div className="divide-y divide-slate-100" data-id="element-328">
                      {listItems.map((item, idx) => <div key={item.id} className="relative overflow-hidden" data-id="element-329">
                          <div className="absolute inset-0 bg-red-500 flex items-center justify-end pr-5 text-white" data-id="element-330">
                            <Trash2Icon size={18} data-id="element-331" />
                          </div>
                          <motion.div initial={{
                    opacity: 0,
                    y: 6
                  }} animate={{
                    opacity: 1,
                    y: 0
                  }} transition={{
                    delay: 0.3 + idx * 0.05
                  }} drag="x" dragConstraints={{
                    left: 0,
                    right: 0
                  }} dragElastic={{
                    left: 0.7,
                    right: 0
                  }} onDragEnd={(e, info) => {
                    if (info.offset.x < -80) removeListItem(item.id);
                  }} className={`relative pl-3 pr-3 py-2 flex items-center gap-2.5 transition-colors cursor-grab active:cursor-grabbing ${item.checked ? 'bg-slate-50' : 'bg-white'}`} data-id="element-332">
                            <div className="flex-1 flex items-center gap-2.5 min-w-0" data-id="element-333">
                              <div className={`w-8 h-8 rounded-md shrink-0 ${item.thumb} opacity-80`} data-id="element-334" />
                              <div className="flex-1 min-w-0" data-id="element-335">
                                <p className={`text-[14px] font-medium truncate leading-tight ${item.checked ? 'text-slate-400 line-through' : 'text-slate-900'}`} data-id="element-336">
                                  {item.name}
                                </p>
                                <div className="flex items-center text-blue-600 text-[11px] font-medium mt-0.5" data-id="element-337">
                                  <MapPinIcon size={10} className="mr-0.5" data-id="element-338" />
                                  {item.aisle}
                                </div>
                              </div>
                            </div>
                            <button onClick={() => toggleListItem(item.id)} className={`w-5 h-5 rounded border flex items-center justify-center shrink-0 transition-colors ${item.checked ? 'bg-blue-600 border-blue-600 text-white' : 'border-slate-300 bg-white hover:border-blue-400'}`} aria-label="Check off item" data-id="element-339">
                              {item.checked && <CheckIcon size={14} strokeWidth={3} data-id="element-340" />}
                            </button>
                          </motion.div>
                        </div>)}
                    </div>
                  </div>
                </motion.div>}
            </motion.div> : mode === 'diy-steps' ? <motion.div key="diy-steps" initial={{
          y: '100%'
        }} animate={{
          y: 0,
          transition: {
            duration: 0.55,
            ease: [0.16, 1, 0.3, 1]
          }
        }} exit={{
          y: '100%',
          transition: {
            duration: 0.4,
            ease: 'easeIn'
          }
        }} className="absolute inset-0 bg-white flex flex-col overflow-y-auto pt-2 pb-4" data-id="element-341">
              <div className="flex items-center justify-between mb-3 shrink-0" data-id="element-342">
                <button onClick={handleBackToMenu} className="flex items-center gap-1 -ml-2 px-2 py-1.5 rounded-full text-slate-600 hover:bg-slate-100 transition-colors text-sm font-medium" aria-label="Back to options" data-id="element-343">
                  <ChevronLeftIcon size={18} data-id="element-344" />
                  Back
                </button>
              </div>

              {/* Instructions "image" — a shareable step-by-step card (tap to open full guide) */}
              <button type="button" onClick={() => setMode('diy-guide-detail')} className="relative w-full text-left rounded-3xl border border-slate-200 bg-gradient-to-b from-slate-50 to-white shadow-[0_4px_20px_rgba(15,23,42,0.06)] overflow-hidden hover:border-blue-200 hover:shadow-[0_6px_24px_rgba(15,23,42,0.1)] transition-all" data-id="element-345">
                {/* Header band */}
                <div className="bg-blue-600 px-5 pt-4 pb-5 text-white relative" data-id="element-346">
                  {ronaSuggestions[0] && <span role="link" tabIndex={0} aria-label={`${t('welcome.ronaGuidesTitle')}: ${ronaSuggestions[0].title}`} title={ronaSuggestions[0].title} onClick={e => {
                e.stopPropagation();
                window.open(ronaSuggestions[0].url, '_blank', 'noopener,noreferrer');
              }} onKeyDown={e => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  e.stopPropagation();
                  window.open(ronaSuggestions[0].url, '_blank', 'noopener,noreferrer');
                }
              }} className="absolute top-3 right-3 w-8 h-8 rounded-lg bg-white/15 hover:bg-white/25 flex items-center justify-center cursor-pointer transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-white/70" data-id="element-347">
                      <ExternalLinkIcon size={15} className="text-white" data-id="element-348" />
                    </span>}
                  <div className="flex items-center gap-2 mb-1.5" data-id="element-349">
                    <div className="w-7 h-7 rounded-lg bg-white/20 flex items-center justify-center" data-id="element-350">
                      <WrenchIcon size={16} data-id="element-351" />
                    </div>
                    <span className="text-[11px] font-semibold uppercase tracking-wider text-blue-100" data-id="element-352">
                      DIY Guide
                    </span>
                  </div>
                  <h2 className="text-lg font-bold leading-tight" data-id="element-353">
                    {t('welcome.diyStepsTitle')}
                  </h2>
                  <p className="text-blue-100 text-xs mt-1" data-id="element-354">
                    {t('welcome.diyStepsSubtitle')}
                  </p>
                </div>

                {/* Steps */}
                <div className="px-5 py-4 space-y-3" data-id="element-355">
                  {[t('welcome.diyStep1'), t('welcome.diyStep2'), t('welcome.diyStep3'), t('welcome.diyStep4'), t('welcome.diyStep5'), t('welcome.diyStep6')].map((step, idx) => <motion.div key={idx} initial={{
                opacity: 0,
                x: -8
              }} animate={{
                opacity: 1,
                x: 0
              }} transition={{
                delay: 0.25 + idx * 0.08
              }} className="flex gap-3 items-start" data-id="element-356">
                      <div className="w-6 h-6 rounded-full bg-blue-100 text-blue-700 text-xs font-bold flex items-center justify-center shrink-0 mt-0.5" data-id="element-357">
                        {idx + 1}
                      </div>
                      <p className="text-[14px] text-slate-700 leading-snug flex-1" data-id="element-358">
                        {step}
                      </p>
                    </motion.div>)}
                </div>

                {/* Subtle Docent Labs logo callout */}
                <div className="px-5 pb-3 pt-1 flex items-center justify-end gap-1.5" data-id="element-359">
                  <span className="w-4 h-4 rounded-[5px] bg-slate-300 flex items-center justify-center" data-id="element-360">
                    <SparklesIcon size={9} className="text-white" data-id="element-361" />
                  </span>
                  <span className="text-[10px] font-medium text-slate-400 tracking-wide" data-id="element-362">
                    {t('welcome.diyPoweredBy')}
                  </span>
                </div>

                {/* "See full guide" footer — white section within the same button */}
                <div className="bg-white border-t border-slate-100 px-4 py-3 flex items-center gap-3" data-id="element-363">
                  <div className="w-9 h-9 rounded-xl bg-blue-50 text-blue-600 flex items-center justify-center shrink-0" data-id="element-364">
                    <BookOpenIcon size={18} data-id="element-365" />
                  </div>
                  <div className="flex-1 min-w-0" data-id="element-366">
                    <p className="text-[14px] font-bold text-slate-900 leading-tight" data-id="element-367">
                      {t('welcome.diyViewFullCallout')}
                    </p>
                    <p className="text-[11px] text-slate-500 leading-snug mt-0.5" data-id="element-368">
                      {t('welcome.diyViewFullCalloutNote')}
                    </p>
                  </div>
                  <ChevronRightIcon size={20} className="shrink-0 text-blue-600" data-id="element-369" />
                </div>
              </button>

              {/* Share button */}
              <motion.button whileTap={{
            scale: 0.98
          }} onClick={handleShareInstructions} className={`mt-3 w-full flex items-center justify-center gap-2 rounded-2xl py-3.5 font-semibold text-base transition-colors ${shared ? 'bg-green-500 text-white' : 'bg-white text-blue-600 border-2 border-blue-100 hover:border-blue-200 hover:bg-slate-50'}`} data-id="element-370">
                {shared ? <>
                    <CheckIcon size={18} strokeWidth={2.5} data-id="element-371" />
                    Shared
                  </> : <>
                    <Share2Icon size={18} data-id="element-372" />
                    {t('welcome.diyShare')}
                  </>}
              </motion.button>
            </motion.div> : mode === 'diy-guide-detail' ? <motion.div key="diy-guide-detail" initial={{
          y: '100%'
        }} animate={{
          y: 0,
          transition: {
            duration: 0.55,
            ease: [0.16, 1, 0.3, 1]
          }
        }} exit={{
          y: '100%',
          transition: {
            duration: 0.4,
            ease: 'easeIn'
          }
        }} className="absolute inset-0 bg-white flex flex-col overflow-y-auto pt-2 pb-4" data-id="element-373">
              <div className="flex items-center justify-between mb-3 shrink-0" data-id="element-374">
                <button onClick={() => setMode('diy-steps')} className="flex items-center gap-1 -ml-2 px-2 py-1.5 rounded-full text-slate-600 hover:bg-slate-100 transition-colors text-sm font-medium" aria-label="Back to summary" data-id="element-375">
                  <ChevronLeftIcon size={18} data-id="element-376" />
                  Back
                </button>
              </div>

              <div className="mb-4" data-id="element-377">
                <div className="flex items-center gap-2 mb-1.5" data-id="element-378">
                  <div className="w-7 h-7 rounded-lg bg-blue-50 text-blue-600 flex items-center justify-center" data-id="element-379">
                    <WrenchIcon size={16} data-id="element-380" />
                  </div>
                  <span className="text-[11px] font-semibold uppercase tracking-wider text-slate-400" data-id="element-381">
                    DIY Guide
                  </span>
                </div>
                <h2 className="text-xl font-bold text-slate-900 leading-tight" data-id="element-382">
                  {t('welcome.diyStepsTitle')}
                </h2>
                <p className="text-slate-500 text-xs mt-0.5" data-id="element-383">
                  {t('welcome.diyStepsSubtitle')}
                </p>
              </div>

              <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 mb-4" data-id="element-384">
                <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-400 mb-1" data-id="element-385">
                  {t('welcome.diyGuideTools')}
                </p>
                <p className="text-[13px] text-slate-700 leading-snug" data-id="element-386">
                  {t('welcome.diyGuideToolsList')}
                </p>
              </div>

              <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-400 mb-2 px-1" data-id="element-387">
                {t('welcome.diyGuideStepsLabel')}
              </p>
              <div className="space-y-4" data-id="element-388">
                {[{
              title: t('welcome.diyDetail1Title'),
              body: t('welcome.diyDetail1Body'),
              tip: t('welcome.diyDetail1Tip')
            }, {
              title: t('welcome.diyDetail2Title'),
              body: t('welcome.diyDetail2Body'),
              warn: t('welcome.diyDetail2Warn')
            }, {
              title: t('welcome.diyDetail3Title'),
              body: t('welcome.diyDetail3Body')
            }, {
              title: t('welcome.diyDetail4Title'),
              body: t('welcome.diyDetail4Body'),
              tip: t('welcome.diyDetail4Tip')
            }, {
              title: t('welcome.diyDetail5Title'),
              body: t('welcome.diyDetail5Body')
            }, {
              title: t('welcome.diyDetail6Title'),
              body: t('welcome.diyDetail6Body'),
              warn: t('welcome.diyDetail6Warn')
            }].map((step, idx) => <motion.div key={idx} initial={{
              opacity: 0,
              y: 8
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              delay: 0.15 + idx * 0.06
            }} className="rounded-2xl border border-slate-100 bg-white shadow-[0_2px_12px_rgba(15,23,42,0.04)] p-4" data-id="element-389">
                    <h3 className="text-[15px] font-bold text-slate-900 mb-1" data-id="element-390">
                      {step.title}
                    </h3>
                    <p className="text-[13px] text-slate-600 leading-relaxed" data-id="element-391">
                      {step.body}
                    </p>
                    {step.tip && <div className="mt-2.5 flex gap-2 rounded-xl bg-blue-50 border border-blue-100 px-3 py-2" data-id="element-392">
                        <SparklesIcon size={14} className="text-blue-600 shrink-0 mt-0.5" data-id="element-393" />
                        <p className="text-[12px] text-blue-800 leading-snug" data-id="element-394">
                          <span className="font-semibold" data-id="element-395">
                            {t('welcome.diyGuideTip')}:{' '}
                          </span>
                          {step.tip}
                        </p>
                      </div>}
                    {step.warn && <div className="mt-2.5 flex gap-2 rounded-xl bg-amber-50 border border-amber-200 px-3 py-2" data-id="element-396">
                        <TriangleAlertIcon size={14} className="text-amber-600 shrink-0 mt-0.5" data-id="element-397" />
                        <p className="text-[12px] text-amber-800 leading-snug" data-id="element-398">
                          <span className="font-semibold" data-id="element-399">
                            {t('welcome.diyGuideWarn')}:{' '}
                          </span>
                          {step.warn}
                        </p>
                      </div>}
                  </motion.div>)}
              </div>

              <div className="mt-5 flex items-center justify-center gap-1.5" data-id="element-400">
                <div className="w-4 h-4 rounded-[5px] bg-slate-300 flex items-center justify-center" data-id="element-401">
                  <SparklesIcon size={9} className="text-white" data-id="element-402" />
                </div>
                <span className="text-[10px] font-medium text-slate-400 tracking-wide" data-id="element-403">
                  {t('welcome.diyPoweredBy')}
                </span>
              </div>

              <motion.button whileTap={{
            scale: 0.98
          }} onClick={handleShareInstructions} className={`mt-4 w-full flex items-center justify-center gap-2 rounded-2xl py-3.5 font-semibold text-base transition-colors ${shared ? 'bg-green-500 text-white' : 'bg-white text-blue-600 border-2 border-blue-100 hover:border-blue-200 hover:bg-slate-50'}`} data-id="element-404">
                {shared ? <>
                    <CheckIcon size={18} strokeWidth={2.5} data-id="element-405" />
                    Shared
                  </> : <>
                    <Share2Icon size={18} data-id="element-406" />
                    {t('welcome.diyShare')}
                  </>}
              </motion.button>
            </motion.div> : mode === 'contractor-summary' ? <motion.div key="contractor-summary" initial={{
          y: '100%'
        }} animate={{
          y: 0,
          transition: {
            duration: 0.55,
            ease: [0.16, 1, 0.3, 1]
          }
        }} exit={{
          y: '100%',
          transition: {
            duration: 0.4,
            ease: 'easeIn'
          }
        }} className="absolute inset-0 bg-white flex flex-col overflow-y-auto pt-2 pb-4" data-id="element-407">
              <div className="flex items-center justify-between mb-3 shrink-0" data-id="element-408">
                <button onClick={handleBackToMenu} className="flex items-center gap-1 -ml-2 px-2 py-1.5 rounded-full text-slate-600 hover:bg-slate-100 transition-colors text-sm font-medium" aria-label="Back to options" data-id="element-409">
                  <ChevronLeftIcon size={18} data-id="element-410" />
                  Back
                </button>
              </div>

              {/* Project summary card — content scrolls in its own small window */}
              <div className="shrink-0 rounded-2xl border border-slate-200 bg-white shadow-[0_2px_12px_rgba(15,23,42,0.04)] overflow-hidden flex flex-col" data-id="element-411">
                <div className="px-5 pt-4 pb-4 max-h-[170px] overflow-y-auto" data-id="element-412">
                  <div className="flex items-center gap-2 mb-1.5" data-id="element-413">
                    <div className="w-7 h-7 rounded-lg bg-blue-50 text-blue-600 flex items-center justify-center" data-id="element-414">
                      <HardHatIcon size={16} data-id="element-415" />
                    </div>
                    <span className="text-[11px] font-semibold uppercase tracking-wider text-slate-400" data-id="element-416">
                      {t('welcome.contractorSummaryLabel')}
                    </span>
                  </div>
                  <h2 className="text-lg font-bold text-slate-900 leading-tight" data-id="element-417">
                    {t('welcome.contractorSummaryTitle')}
                  </h2>
                  <p className="text-[13px] text-slate-600 leading-snug mt-1.5" data-id="element-418">
                    {t('welcome.contractorSummaryBody')}
                  </p>

                  <div className="mt-3 pt-3 border-t border-slate-100" data-id="element-419">
                    <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-400 mb-1.5" data-id="element-420">
                      {t('welcome.contractorScope')}
                    </p>
                    <ul className="space-y-1" data-id="element-421">
                      {[t('welcome.contractorScope1'), t('welcome.contractorScope2'), t('welcome.contractorScope3')].map((s, i) => <li key={i} className="flex items-center gap-2 text-[13px] text-slate-700" data-id="element-422">
                          <CheckIcon size={14} strokeWidth={2.5} className="text-blue-600 shrink-0" data-id="element-423" />
                          {s}
                        </li>)}
                    </ul>
                  </div>
                </div>

                {/* Estimate band */}
                <div className="bg-slate-50 border-t border-slate-100 px-5 py-3 flex items-end justify-between" data-id="element-424">
                  <div data-id="element-425">
                    <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-400" data-id="element-426">
                      {t('welcome.contractorEstimateLabel')}
                    </p>
                    <p className="text-[10px] text-slate-400 mt-0.5" data-id="element-427">
                      {t('welcome.contractorEstimateNote')}
                    </p>
                  </div>
                  <span className="text-xl font-bold text-slate-900" data-id="element-428">
                    $4,800–$7,200
                  </span>
                </div>
              </div>

              {/* Contact form / confirmation */}
              <AnimatePresence mode="wait" data-id="element-429">
                {contractorSubmitted ? <motion.div key="submitted" initial={{
              opacity: 0,
              y: 8
            }} animate={{
              opacity: 1,
              y: 0
            }} className="mt-3 rounded-2xl border-2 border-green-300 bg-green-50 px-4 py-4 flex items-start gap-3" data-id="element-430">
                    <div className="w-9 h-9 rounded-full bg-green-500 flex items-center justify-center shrink-0" data-id="element-431">
                      <CheckIcon size={20} strokeWidth={3} className="text-white" data-id="element-432" />
                    </div>
                    <div className="flex-1 min-w-0" data-id="element-433">
                      <p className="text-[14px] font-bold text-green-900 leading-tight" data-id="element-434">
                        {t('welcome.contractorSubmittedTitle')}
                      </p>
                      <p className="text-[12px] text-green-800 leading-snug mt-0.5" data-id="element-435">
                        {t('welcome.contractorSubmittedNote')}
                      </p>
                    </div>
                  </motion.div> : <motion.form key="form" onSubmit={handleContractorSubmit} initial={{
              opacity: 0,
              y: 8
            }} animate={{
              opacity: 1,
              y: 0
            }} exit={{
              opacity: 0
            }} className="mt-3 rounded-2xl border border-slate-200 bg-white shadow-[0_2px_12px_rgba(15,23,42,0.04)] px-4 py-4 space-y-3" data-id="element-436">
                    <div className="flex items-start gap-2" data-id="element-437">
                      <PhoneIcon size={16} className="text-blue-600 shrink-0 mt-0.5" data-id="element-438" />
                      <p className="text-[13px] text-slate-600 leading-snug" data-id="element-439">
                        {t('welcome.contractorFormNote')}
                      </p>
                    </div>

                    <div data-id="element-440">
                      <label className="block text-[11px] font-semibold uppercase tracking-wider text-slate-400 mb-1" data-id="element-441">
                        {t('welcome.contractorPhoneLabel')}
                      </label>
                      <input type="tel" required value={contractorPhone} onChange={e => setContractorPhone(e.target.value)} placeholder={t('welcome.contractorPhonePlaceholder')} className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-2.5 text-[15px] text-slate-900 placeholder:text-slate-400 outline-none focus:border-blue-300 focus:ring-2 focus:ring-blue-100 transition-all" data-id="element-442" />
                    </div>

                    <div data-id="element-443">
                      <label className="block text-[11px] font-semibold uppercase tracking-wider text-slate-400 mb-1" data-id="element-444">
                        {t('welcome.contractorZipLabel')}
                      </label>
                      <input type="text" required value={contractorZip} onChange={e => setContractorZip(e.target.value)} placeholder={t('welcome.contractorZipPlaceholder')} className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-2.5 text-[15px] text-slate-900 placeholder:text-slate-400 outline-none focus:border-blue-300 focus:ring-2 focus:ring-blue-100 transition-all" data-id="element-445" />
                      <p className="text-[11px] text-slate-400 mt-1 flex items-center gap-1" data-id="element-446">
                        <MapPinIcon size={11} data-id="element-447" />
                        {t('welcome.contractorZipHint')}
                      </p>
                    </div>

                    <motion.button whileTap={{
                scale: 0.98
              }} type="submit" disabled={!contractorPhone.trim() || !contractorZip.trim()} className="w-full flex items-center justify-center gap-2 rounded-2xl py-3.5 font-semibold text-base bg-blue-600 text-white hover:bg-blue-700 disabled:bg-slate-200 disabled:text-slate-400 disabled:cursor-not-allowed transition-colors" data-id="element-448">
                      {t('welcome.contractorSubmit')}
                    </motion.button>

                    {/* Matched contractors — appear once a ZIP is entered */}
                    <AnimatePresence data-id="element-449">
                      {contractorZip.trim().length >= 3 && <motion.div key="contractor-matches" initial={{
                  opacity: 0,
                  height: 0
                }} animate={{
                  opacity: 1,
                  height: 'auto'
                }} exit={{
                  opacity: 0,
                  height: 0
                }} transition={{
                  duration: 0.3
                }} className="overflow-hidden" data-id="element-450">
                          <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-400 mb-1.5 pt-1" data-id="element-451">
                            {t('welcome.contractorMatchesTitle')}
                          </p>
                          <div className="space-y-2" data-id="element-452">
                            {[{
                      name: 'Maple Ridge Decks & Carpentry',
                      rating: 4.9,
                      reviews: 212
                    }, {
                      name: 'Northgate Home Improvements',
                      rating: 4.7,
                      reviews: 168
                    }, {
                      name: 'Summit Outdoor Builders',
                      rating: 4.5,
                      reviews: 94
                    }].map((c, idx) => <motion.div key={c.name} initial={{
                      opacity: 0,
                      x: -8
                    }} animate={{
                      opacity: 1,
                      x: 0
                    }} transition={{
                      delay: idx * 0.08
                    }} className="flex items-center gap-3 rounded-xl border border-slate-100 bg-white px-3 py-2.5 shadow-[0_1px_6px_rgba(15,23,42,0.04)]" data-id="element-453">
                                <div className="w-9 h-9 rounded-lg bg-blue-50 text-blue-600 flex items-center justify-center shrink-0 font-bold text-[14px]" data-id="element-454">
                                  {c.name.charAt(0)}
                                </div>
                                <div className="flex-1 min-w-0" data-id="element-455">
                                  <p className="text-[13px] font-bold text-slate-900 leading-tight truncate" data-id="element-456">
                                    {c.name}
                                  </p>
                                  <div className="flex items-center gap-1 mt-0.5" data-id="element-457">
                                    <div className="flex items-center" data-id="element-458">
                                      {[0, 1, 2, 3, 4].map(s => <StarIcon key={s} size={11} className={s < Math.round(c.rating) ? 'text-amber-400 fill-amber-400' : 'text-slate-200 fill-slate-200'} data-id="element-459" />)}
                                    </div>
                                    <span className="text-[11px] font-semibold text-slate-700" data-id="element-460">
                                      {c.rating.toFixed(1)}
                                    </span>
                                    <span className="text-[11px] text-slate-400" data-id="element-461">
                                      ({c.reviews})
                                    </span>
                                  </div>
                                </div>
                              </motion.div>)}
                          </div>
                          <p className="text-[11px] text-slate-400 mt-1.5 flex items-center gap-1" data-id="element-462">
                            <CheckIcon size={11} className="text-blue-600" data-id="element-463" />
                            {t('welcome.contractorMatchesNote')}
                          </p>
                        </motion.div>}
                    </AnimatePresence>
                  </motion.form>}
              </AnimatePresence>
            </motion.div> : <motion.div key="list" initial={{
          y: '100%'
        }} animate={{
          y: 0,
          transition: {
            duration: 0.55,
            ease: [0.16, 1, 0.3, 1]
          }
        }} exit={listExitUp ? {
          y: '-120%',
          transition: {
            duration: 0.55,
            ease: [0.7, 0, 0.84, 0]
          }
        } : {
          y: '100%',
          transition: {
            duration: 0.4,
            ease: 'easeIn'
          }
        }} className="absolute inset-0 bg-white flex flex-col overflow-y-auto pt-2 pb-4" data-id="element-464">
              <div className="flex items-center justify-between mb-2" data-id="element-465">
                <button onClick={handleBackToMenu} className="flex items-center gap-1 -ml-2 px-2 py-1.5 rounded-full text-slate-600 hover:bg-slate-100 transition-colors text-sm font-medium" aria-label="Back to options" data-id="element-466">
                  <ChevronLeftIcon size={18} data-id="element-467" />
                  Back
                </button>
                <span className="text-xs font-semibold text-slate-400 uppercase tracking-wider" data-id="element-468">
                  {listItems.filter(i => i.checked).length}/{listItems.length}
                </span>
              </div>

              {/* Condensed chat summary — the chat box "shrunk" to one line */}
              {messages.some(m => m.role === 'user') && <motion.div layout initial={{
            opacity: 0,
            y: -6
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.35,
            ease: 'easeOut'
          }} className="mb-2 flex items-center gap-2 bg-blue-50 border border-blue-100 rounded-full px-3 py-1.5 text-blue-700 text-xs font-medium" data-id="element-469">
                  <MessageCircleIcon size={12} className="shrink-0" data-id="element-470" />
                  <span className="truncate" data-id="element-471">
                    <span className="font-semibold mr-1 text-blue-500" data-id="element-472">
                      {t('welcome.projectSummary')}:
                    </span>
                    {[...messages].reverse().find(m => m.role === 'user')?.text}
                  </span>
                </motion.div>}

              <div className="mb-2" data-id="element-473">
                <h2 className="text-xl font-bold text-slate-900 tracking-tight leading-tight" data-id="element-474">
                  {t('welcome.listTitle')}{' '}
                  <span className="text-xs font-medium text-slate-400" data-id="element-475">
                    ({t('welcome.swipeHint')})
                  </span>
                </h2>
                <p className="text-slate-500 text-xs mt-0.5" data-id="element-476">
                  {t('welcome.listSubtitle')}
                </p>
              </div>

              <div className="bg-white rounded-2xl border border-slate-200 shadow-[0_2px_12px_rgba(15,23,42,0.04)] overflow-hidden shrink-0 mr-5" data-id="element-477">
                <div className="divide-y divide-slate-100 max-h-[40vh] overflow-y-auto" data-id="element-478">
                  {listItems.map((item, idx) => <div key={item.id} className="relative overflow-hidden" data-id="element-479">
                      <div className="absolute inset-0 bg-red-500 flex items-center justify-end pr-5 text-white" data-id="element-480">
                        <Trash2Icon size={18} data-id="element-481" />
                      </div>
                      <motion.div initial={{
                  opacity: 0,
                  y: 6
                }} animate={{
                  opacity: 1,
                  y: 0
                }} transition={{
                  delay: 0.15 + idx * 0.05
                }} drag="x" dragConstraints={{
                  left: 0,
                  right: 0
                }} dragElastic={{
                  left: 0.7,
                  right: 0
                }} onDragEnd={(e, info) => {
                  if (info.offset.x < -80) removeListItem(item.id);
                }} className={`relative pl-2 pr-3 py-2 flex items-center gap-2.5 transition-colors cursor-grab active:cursor-grabbing ${item.checked ? 'bg-slate-50' : 'bg-white'}`} data-id="element-482">
                        <div className="flex-1 flex items-center gap-2.5 min-w-0" data-id="element-483">
                          <div className={`w-8 h-8 rounded-md shrink-0 ${item.thumb} opacity-80`} data-id="element-484" />
                          <div className="flex-1 min-w-0" data-id="element-485">
                            <p className={`text-[14px] font-medium truncate transition-colors leading-tight ${item.checked ? 'text-slate-400 line-through' : 'text-slate-900'}`} data-id="element-486">
                              {item.name}
                            </p>
                            <div className="flex items-center gap-2 mt-0.5" data-id="element-487">
                              <div className="flex items-center text-blue-600 text-[11px] font-medium" data-id="element-488">
                                <MapPinIcon size={10} className="mr-0.5" data-id="element-489" />
                                {item.aisle}
                              </div>
                              {item.dealCategory && <button onClick={e => {
                          e.stopPropagation();
                          const cat = dealCategories.find(c => c.id === item.dealCategory);
                          if (cat) handleSelectDealCategory(cat);
                        }} className="inline-flex items-center gap-1 rounded-full bg-yellow-400 hover:bg-yellow-300 text-yellow-900 text-[10px] font-bold px-2 py-0.5 transition-colors shrink-0" data-id="element-490">
                                  <TagIcon size={10} fill="currentColor" className="shrink-0" data-id="element-491" />
                                  {t('welcome.onSale')}
                                </button>}
                            </div>
                          </div>
                        </div>
                        <button onClick={() => toggleListItem(item.id)} className={`w-5 h-5 rounded border flex items-center justify-center shrink-0 transition-colors ${item.checked ? 'bg-blue-600 border-blue-600 text-white' : 'border-slate-300 bg-white hover:border-blue-400'}`} aria-label="Check off item" data-id="element-492">
                          {item.checked && <CheckIcon size={14} strokeWidth={3} data-id="element-493" />}
                        </button>
                      </motion.div>
                    </div>)}
                </div>
              </div>
            </motion.div>}
        </AnimatePresence>
      </div>

      {/* BOTTOM / GRAY AREA — slides off the bottom on the contractor summary so it gets the full screen */}
      <AnimatePresence data-id="element-494">
        {mode !== 'contractor-summary' && <motion.div key="bottom-gray" layout initial={{
        y: '120%'
      }} animate={{
        y: 0
      }} exit={{
        y: '120%'
      }} transition={{
        duration: 0.5,
        ease: 'easeInOut'
      }} className="relative mt-auto pt-4 -mx-6 -mb-6 px-6 pb-5 border-t border-slate-200 bg-slate-100 space-y-2.5 flex flex-col items-center" data-id="element-495">
            {/* Helper text — crossfades between "Or describe..." and "Tap to speak / Listening" */}
            <div className="min-h-7 flex items-center justify-center" data-id="element-496">
              <AnimatePresence mode="wait" data-id="element-497">
                {(mode === 'menu' || mode === 'deals' || mode === 'deals-products' || mode === 'find' || mode === 'diy-steps' || mode === 'diy-guide-detail' || mode === 'contractor-summary') && associateStatus !== 'idle' ? <motion.p key="menu-helper-wait" initial={{
              opacity: 0,
              scale: 0.92
            }} animate={{
              opacity: 1,
              scale: 1,
              boxShadow: ['0 0 0 0 rgba(37, 99, 235, 0.45)', '0 0 0 10px rgba(37, 99, 235, 0)']
            }} exit={{
              opacity: 0,
              scale: 0.92
            }} transition={{
              opacity: {
                duration: 0.25
              },
              scale: {
                duration: 0.25
              },
              boxShadow: {
                repeat: Infinity,
                duration: 1.8,
                ease: 'easeOut'
              }
            }} className="text-[13px] font-bold text-blue-700 bg-blue-50 border border-blue-200 rounded-full px-3.5 py-1 text-center leading-tight" data-id="element-498">
                    {t('welcome.helpWhileWaitMic')}
                  </motion.p> : <motion.div key="chat-toggle" initial={{
              opacity: 0
            }} animate={{
              opacity: 1
            }} exit={{
              opacity: 0
            }} transition={{
              duration: 0.25,
              delay: 0.2
            }} className="flex flex-col items-center gap-1" data-id="element-499">
                    <span className="text-sm text-slate-500 whitespace-nowrap" data-id="element-500">
                      {mode === 'list' ? t('welcome.addMoreItems') : t('welcome.talkToAI')}
                    </span>
                    <div className="flex items-center bg-slate-200/80 rounded-full p-0.5" data-id="element-501">
                      <button onClick={() => setInputMode('voice')} className={`flex items-center gap-1.5 px-3 py-1 rounded-full text-[13px] font-medium transition-colors ${inputMode === 'voice' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`} data-id="element-502">
                        <MicIcon size={14} data-id="element-503" />
                        {t('welcome.voice')}
                      </button>
                      <button onClick={() => setInputMode('text')} className={`flex items-center gap-1.5 px-3 py-1 rounded-full text-[13px] font-medium transition-colors ${inputMode === 'text' ? 'bg-red-600 text-white shadow-sm' : 'text-slate-500 hover:text-slate-700'}`} data-id="element-504">
                        <MessageSquareIcon size={14} data-id="element-505" />
                        {t('welcome.text')}
                      </button>
                    </div>
                  </motion.div>}
              </AnimatePresence>
            </div>

            {/* CTA area: Talk-to-AI slides off right; associate status bar slides in from left */}
            <div className="relative w-full flex items-center justify-center py-1" data-id="element-506">
              <AnimatePresence mode="popLayout" initial={false} data-id="element-507">
                {<motion.div key="cta" className="relative w-full flex items-center justify-center" data-id="element-508">
                    {inputMode === 'text' ? <motion.div initial={{
                opacity: 0,
                scale: 0.9
              }} animate={{
                opacity: 1,
                scale: 1
              }} exit={{
                opacity: 0,
                scale: 0.9
              }} className="relative z-10 w-full max-w-sm h-28 flex items-center justify-center px-4" data-id="element-509">
                        <div className="relative w-full" data-id="element-510">
                          <input type="text" value={textInput} onChange={e => setTextInput(e.target.value)} placeholder="Type your message..." className="w-full bg-white border border-slate-200 rounded-full pl-5 pr-12 py-3.5 text-[15px] text-slate-900 placeholder:text-slate-400 outline-none focus:border-blue-300 focus:ring-2 focus:ring-blue-100 transition-all shadow-sm" onKeyDown={e => {
                    if (e.key === 'Enter' && textInput.trim()) {
                      setTextInput('');
                    }
                  }} data-id="element-511" />
                          <button onClick={() => {
                    if (textInput.trim()) {
                      setTextInput('');
                    }
                  }} className="absolute right-2 top-1/2 -translate-y-1/2 w-9 h-9 flex items-center justify-center bg-blue-600 hover:bg-blue-700 text-white rounded-full transition-colors" data-id="element-512">
                            <ChevronRightIcon size={20} data-id="element-513" />
                          </button>
                        </div>
                      </motion.div> : <>
                        {/* Mic-pulse ring */}
                        {(mode === 'chat' || mode === 'list') && isRecording && <motion.div animate={{
                  scale: [1, 1.5, 1],
                  opacity: [0.5, 0, 0.5]
                }} transition={{
                  repeat: Infinity,
                  duration: 1.5
                }} className="absolute w-28 h-28 rounded-full bg-blue-400" data-id="element-514" />}

                        {/* Idle homepage mic pulse */}
                        {(mode === 'menu' || mode === 'deals' || mode === 'deals-products' || mode === 'find' || mode === 'diy-steps' || mode === 'diy-guide-detail') && !isRecording && <motion.div animate={{
                  scale: [1, 1.45, 1],
                  opacity: [0.45, 0, 0.45]
                }} transition={{
                  repeat: Infinity,
                  duration: 2,
                  ease: 'easeInOut'
                }} className="absolute w-28 h-28 rounded-full bg-blue-400" data-id="element-515" />}

                        <motion.button onClick={mode === 'menu' || mode === 'deals' || mode === 'deals-products' || mode === 'find' || mode === 'diy-steps' || mode === 'diy-guide-detail' || mode === 'contractor-summary' ? () => handleTalkToAI() : handleMicTap} disabled={false} transition={{
                  type: 'spring',
                  damping: 26,
                  stiffness: 220
                }} className={isRecording ? 'relative z-10 w-28 h-28 rounded-full flex items-center justify-center bg-red-500 hover:bg-red-600 text-white shadow-lg transition-colors' : 'relative z-10 w-28 h-28 rounded-full flex items-center justify-center bg-blue-600 hover:bg-blue-700 text-white shadow-lg transition-colors'} data-id="element-516">
                          <AnimatePresence mode="wait" data-id="element-517">
                            {mode === 'menu' || mode === 'deals' || mode === 'deals-products' || mode === 'find' || mode === 'diy-steps' || mode === 'diy-guide-detail' || mode === 'contractor-summary' ? <motion.span key="menu-content" initial={{
                      opacity: 0
                    }} animate={{
                      opacity: 1
                    }} exit={{
                      opacity: 0
                    }} transition={{
                      duration: 0.2
                    }} className="flex items-center justify-center" data-id="element-518">
                                <MicIcon size={40} data-id="element-519" />
                              </motion.span> : isRecording ? <motion.span key="recording-content" initial={{
                      opacity: 0
                    }} animate={{
                      opacity: 1
                    }} exit={{
                      opacity: 0
                    }} transition={{
                      duration: 0.2
                    }} className="flex items-center justify-center" data-id="element-520">
                                <SquareIcon size={28} fill="currentColor" data-id="element-521" />
                              </motion.span> : <motion.span key="chat-content" initial={{
                      opacity: 0
                    }} animate={{
                      opacity: 1
                    }} exit={{
                      opacity: 0
                    }} transition={{
                      duration: 0.2
                    }} className="flex items-center justify-center" data-id="element-522">
                                <MicIcon size={40} data-id="element-523" />
                              </motion.span>}
                          </AnimatePresence>
                        </motion.button>
                      </>}
                  </motion.div>}
              </AnimatePresence>
            </div>

            <div className="w-full space-y-2.5" data-id="element-524">
              <motion.div layout className="w-full flex items-stretch gap-2.5" data-id="element-525">
                <motion.div layout className="flex-1 min-w-0" data-id="element-526">
                  <AssociateButton idleLabel={t('welcome.requestAssociate')} data-id="element-527" />
                </motion.div>
                <AnimatePresence mode="popLayout" data-id="element-528">
                  {associateStatus !== 'idle' && <motion.button layout key="associate-arrived" type="button" initial={{
                opacity: 0,
                scale: 0.9
              }} animate={{
                opacity: 1,
                scale: 1
              }} exit={{
                opacity: 0,
                scale: 0.9
              }} transition={{
                duration: 0.28,
                ease: [0.22, 1, 0.36, 1]
              }} whileTap={{
                scale: 0.96
              }} onClick={handleAssociateArrived} disabled={associateArrived} className={`group shrink-0 w-[108px] rounded-2xl flex flex-col items-center justify-center gap-1 px-2.5 text-center transition-colors min-h-[77px] border-2 ${associateArrived ? 'bg-green-500 border-green-600 text-white' : 'bg-white border-blue-200 hover:border-blue-300 hover:bg-slate-50 text-blue-600'}`} data-id="element-529">
                      <span className={`w-7 h-7 rounded-full flex items-center justify-center ${associateArrived ? 'bg-white/20' : 'bg-blue-50 group-hover:bg-blue-100'} transition-colors`} data-id="element-530">
                        {associateArrived ? <CheckIcon size={16} strokeWidth={2.5} data-id="element-531" /> : <UserCheckIcon size={16} data-id="element-532" />}
                      </span>
                      <span className="text-[11px] font-semibold leading-[1.15]" data-id="element-533">
                        {associateArrived ? t('welcome.associateHere') : t('welcome.associateArrivedTap')}
                      </span>
                    </motion.button>}
                </AnimatePresence>
              </motion.div>
            </div>
          </motion.div>}
      </AnimatePresence>
    </motion.div>;
}